import React, { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Label } from '@/components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { CheckCircle, Loader2, Building2, Briefcase, Crown, AlertCircle } from 'lucide-react';
import { useOrganization } from '@/hooks/useOrganization';
import { useSettingspageTranslations } from '@/hooks/useSettingspageTranslations';
import { toast } from 'sonner';
import api from '@/services/api';

interface PlanUpgradeModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

interface PlanConfig {
  name: string;
  monthlyPrice: number;
  annualPrice: number;
  features: string[];
  isPopular?: boolean;
}

export function PlanUpgradeModal({ isOpen, onClose }: PlanUpgradeModalProps) {
  const { organization } = useOrganization();
  const i18n = useSettingspageTranslations();
  const [selectedPlan, setSelectedPlan] = useState<string>('');
  const [billingCycle, setBillingCycle] = useState<'monthly' | 'annual'>('monthly');
  const [isProcessing, setIsProcessing] = useState(false);

  const PLANS: Record<string, PlanConfig> = {
    basic: {
      name: i18n.organization.subscription.plans.basic,
      monthlyPrice: 49,
      annualPrice: 42,
      features: [
        i18n.organization.subscription.modal.planFeatures.basic.skus,
        i18n.organization.subscription.modal.planFeatures.basic.storage,
        i18n.organization.subscription.modal.planFeatures.basic.users,
        i18n.organization.subscription.modal.planFeatures.basic.support,
        i18n.organization.subscription.modal.planFeatures.basic.connectors,
      ],
    },
    business: {
      name: i18n.organization.subscription.plans.business,
      monthlyPrice: 739,
      annualPrice: 628,
      features: [
        i18n.organization.subscription.modal.planFeatures.business.skus,
        i18n.organization.subscription.modal.planFeatures.business.storage,
        i18n.organization.subscription.modal.planFeatures.business.users,
        i18n.organization.subscription.modal.planFeatures.business.support,
        i18n.organization.subscription.modal.planFeatures.business.connectors,
      ],
      isPopular: true,
    },
    enterprise: {
      name: i18n.organization.subscription.plans.enterprise,
      monthlyPrice: 1470,
      annualPrice: 1250,
      features: [
        i18n.organization.subscription.modal.planFeatures.enterprise.skus,
        i18n.organization.subscription.modal.planFeatures.enterprise.storage,
        i18n.organization.subscription.modal.planFeatures.enterprise.users,
        i18n.organization.subscription.modal.planFeatures.enterprise.support,
        i18n.organization.subscription.modal.planFeatures.enterprise.connectors,
      ],
    },
  };

  // Reset selection when modal opens
  React.useEffect(() => {
    if (isOpen && organization) {
      // Pre-select the next tier up
      if (organization.plan === 'basic') {
        setSelectedPlan('business');
      } else if (organization.plan === 'business') {
        setSelectedPlan('enterprise');
      } else {
        setSelectedPlan('business');
      }
    }
  }, [isOpen, organization]);

  const handleUpgrade = async () => {
    if (!selectedPlan || !organization) return;

    try {
      setIsProcessing(true);

      // Create Stripe checkout session for upgrade
      const response = await api.post('/api/organizations/stripe/create-checkout-session/', {
        email: '', // Will be handled by backend from authenticated user
        org_name: organization.name,
        plan: selectedPlan,
        billing_cycle: billingCycle,
        is_upgrade: true,
      });

      if (response.data?.checkout_url) {
        // Redirect to Stripe checkout
        window.location.href = response.data.checkout_url;
      } else {
        throw new Error('No checkout URL received');
      }
    } catch (error: any) {
      toast.error(error.response?.data?.error || 'Failed to start upgrade process');
      setIsProcessing(false);
    }
  };

  const getPlanIcon = (plan: string) => {
    switch (plan) {
      case 'basic':
        return <Building2 className="size-5" />;
      case 'business':
        return <Briefcase className="size-5" />;
      case 'enterprise':
        return <Crown className="size-5" />;
      default:
        return null;
    }
  };

  const getUpgradePrice = (plan: string) => {
    const config = PLANS[plan];
    if (!config || !organization) return 0;

    const newPrice = billingCycle === 'annual' ? config.annualPrice : config.monthlyPrice;
    const currentConfig = PLANS[organization.plan];
    const currentPrice =
      organization.billing_cycle === 'annual'
        ? currentConfig?.annualPrice || 0
        : currentConfig?.monthlyPrice || 0;

    return Math.max(0, newPrice - currentPrice);
  };

  const availablePlans = Object.entries(PLANS).filter(
    ([key]) => key !== organization?.plan && key !== 'pro' // Exclude current plan and pro
  );

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl">
        <DialogHeader>
          <DialogTitle>{i18n.organization.subscription.modal.title}</DialogTitle>
          <DialogDescription>{i18n.organization.subscription.modal.description}</DialogDescription>
        </DialogHeader>

        <div className="space-y-6">
          {/* Billing Cycle Toggle */}
          <Tabs
            value={billingCycle}
            onValueChange={v => setBillingCycle(v as 'monthly' | 'annual')}
          >
            <TabsList className="grid w-full max-w-md grid-cols-2">
              <TabsTrigger value="monthly">
                {i18n.organization.subscription.billing.monthly}
              </TabsTrigger>
              <TabsTrigger value="annual">
                {i18n.organization.subscription.billing.annual}{' '}
                <Badge variant="secondary" className="ml-2">
                  {i18n.organization.subscription.modal.annualDiscountShort}
                </Badge>
              </TabsTrigger>
            </TabsList>

            <TabsContent value={billingCycle} className="mt-6">
              <RadioGroup value={selectedPlan} onValueChange={setSelectedPlan}>
                <div className="grid gap-4 md:grid-cols-2">
                  {availablePlans.map(([key, plan]) => {
                    const isCurrentPlan = key === organization?.plan;
                    const price = billingCycle === 'annual' ? plan.annualPrice : plan.monthlyPrice;

                    return (
                      <Card
                        key={key}
                        className={`relative cursor-pointer transition-all ${
                          selectedPlan === key
                            ? 'border-primary-500 shadow-lg'
                            : 'border-gray-200 hover:border-gray-300'
                        } ${isCurrentPlan ? 'opacity-50' : ''}`}
                      >
                        {plan.isPopular && (
                          <Badge className="absolute -top-3 right-4" variant="default">
                            {i18n.organization.subscription.modal.mostPopular}
                          </Badge>
                        )}

                        <Label htmlFor={key} className="cursor-pointer">
                          <CardHeader>
                            <div className="flex items-center justify-between">
                              <div className="flex items-center space-x-3">
                                <div className="rounded-lg bg-primary-50 p-2">
                                  {getPlanIcon(key)}
                                </div>
                                <div>
                                  <CardTitle className="text-lg">{plan.name}</CardTitle>
                                  <CardDescription>
                                    €{price}/month
                                    {billingCycle === 'annual' &&
                                      ` (${i18n.organization.subscription.messages.billedAnnually})`}
                                  </CardDescription>
                                </div>
                              </div>
                              <RadioGroupItem value={key} id={key} disabled={isCurrentPlan} />
                            </div>
                          </CardHeader>
                          <CardContent>
                            <ul className="space-y-2">
                              {plan.features.map((feature, i) => (
                                <li key={i} className="flex items-start text-sm">
                                  <CheckCircle className="mr-2 mt-0.5 size-4 shrink-0 text-green-500" />
                                  <span>{feature}</span>
                                </li>
                              ))}
                            </ul>
                          </CardContent>
                        </Label>
                      </Card>
                    );
                  })}
                </div>
              </RadioGroup>
            </TabsContent>
          </Tabs>

          {/* Pricing Summary */}
          {selectedPlan && (
            <Card className="border-primary-200 bg-primary-50/50">
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div className="space-y-1">
                    <p className="text-sm font-medium">
                      {i18n.organization.subscription.modal.upgradeSummary}
                    </p>
                    <p className="text-sm text-gray-600">
                      {i18n.organization.subscription.modal.upgradingFromTo
                        .replace('{{currentPlan}}', organization?.plan || '')
                        .replace('{{newPlan}}', selectedPlan)}
                    </p>
                  </div>
                  <div className="text-right">
                    <p className="text-2xl font-bold">
                      €{getUpgradePrice(selectedPlan)}
                      <span className="text-sm font-normal text-gray-600">/month</span>
                    </p>
                    {billingCycle === 'annual' && (
                      <p className="text-sm text-green-600">
                        {i18n.organization.subscription.modal.annualDiscount}
                      </p>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Info Alert */}
          <Alert>
            <AlertCircle className="size-4" />
            <AlertDescription>{i18n.organization.subscription.modal.upgradeInfo}</AlertDescription>
          </Alert>

          {/* Action Buttons */}
          <div className="flex justify-end space-x-3">
            <Button variant="outline" onClick={onClose} disabled={isProcessing}>
              {i18n.organization.subscription.modal.cancel}
            </Button>
            <Button
              onClick={handleUpgrade}
              disabled={!selectedPlan || isProcessing}
              className="min-w-[120px]"
            >
              {isProcessing ? (
                <>
                  <Loader2 className="mr-2 size-4 animate-spin" />
                  Processing...
                </>
              ) : (
                i18n.organization.subscription.upgradeNow
              )}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
